// Offer to shrink the images someone just attached.
//
// [Brian 2026-10-01] "when attaching images user would be prompted with an optional image optimizing
// modal with options of jpg or avif."
//
// 🚩 OPTIONAL is the whole brief, so "Attach as they are" is a real button and not a cancel-X in the
// corner, and closing the modal any other way keeps the originals too. The one thing this must never
// do is quietly change what someone chose to send.
//
// 🔴 The conversion runs on the SERVER. `canvas.toBlob(b, 'image/avif')` silently returns a PNG —
// verified in Chrome 147 — so a browser-side version of this would have attached a PNG named .avif,
// usually larger than the original. Measured on a 2.7 MB phone screenshot: JPEG q80 → 152 KB in
// 0.4 s, AVIF q50 → 72 KB in 1.2 s.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Image as ImageIcon, X } from 'lucide-react';
import { optimizeImage } from '@/api/images';
import { MAX_TOTAL_ATTACHMENT_BYTES, type Attachment } from './compose-editor';
// The rule that decides whether this modal appears lives in lib/, where a unit test can reach it.
import { isOptimizableImage, formatBytes } from '@/lib/image-optimize-rules';
import { focusableIn, handleTrapKey } from '@/lib/focus-trap';
import { ImageProgress } from './image-progress';
import type { FileProgress } from '@/lib/image-progress-math';
export { isOptimizableImage, formatBytes } from '@/lib/image-optimize-rules';


interface Props {
  /** The images being offered for conversion. Non-images never reach here. */
  images: Attachment[];
  /** Everything attached in the same action that is NOT an image, passed straight through. */
  passthrough: Attachment[];
  /**
   * Called EXACTLY ONCE with the final set to attach, plus any per-file notes.
   * 🚩 `notes` exists because this modal unmounts the moment it calls back, so it cannot show them
   * itself — the caller toasts them. Without that, "HEIC support is not installed on the server"
   * rendered for zero frames and nobody ever saw it.
   */
  onDone: (all: Attachment[], notes: string[]) => void;
  /**
   * True when the originals do not fit under the attachment cap, so converting is the only way
   * these files attach at all.
   * 🚩 The offer is OPTIONAL by Brian's brief everywhere else; this is the one case where it is not,
   * and the modal says so rather than showing a "keep them as they are" button that cannot work.
   */
  required?: boolean;
}

export function ImageOptimizeModal({ images, passthrough, onDone, required = false }: Props) {
  const [format, setFormat] = useState<'jpeg' | 'avif'>('jpeg');
  const [busy, setBusy] = useState(false);
  // Per-file state, so the progress view can show each upload's own percentage rather than one
  // bar for the batch. Seeded from `images` and mutated in place by index as `run()` walks them.
  const [progress, setProgress] = useState<FileProgress[]>(
    () => images.map((a) => ({ filename: a.filename, size: a.size, phase: 'queued', uploaded: 0 })),
  );
  const patch = (i: number, next: Partial<FileProgress>) =>
    setProgress((list) => list.map((f, n) => (n === i ? { ...f, ...next } : f)));

  const total = images.reduce((s, a) => s + a.size, 0);

  // 🔴 THE BUG THIS GUARDS. Closing mid-conversion used to attach everything TWICE: the close
  // handed the originals back and unmounted the modal, but `run()` kept going in its closure and
  // called `onDone` again with the converted set. `setAttachments` is a stable dispatcher, so the
  // second append landed — every passthrough file duplicated, every image attached as both the
  // original AND the converted copy, blowing the 20 MB cap that is checked before conversion.
  // A ref, not state: it must be true for the in-flight closure immediately, not after a re-render.
  const settled = useRef(false);
  const finish = (all: Attachment[], notes: string[] = []) => {
    if (settled.current) return;
    settled.current = true;
    onDone(all, notes);
  };
  const keepOriginals = () => finish([...passthrough, ...images]);
  // 🚩 Over the cap there is no "keep them" outcome: the originals cannot be attached, so dismissing
  // attaches NOTHING. `finish([])` still runs the one-shot guard and clears the offer in the parent.
  const cancel = () => (required ? finish([]) : keepOriginals());

  const panel = useRef<HTMLDivElement>(null);

  // Keyboard containment.
  //
  // 🔴 Without this, Tab walked straight out of the modal and into the Send button behind it, and
  // Enter sent the email WITHOUT the images being offered — then `onDone` appended them to a
  // compose screen that was already navigating away. The overlay stops the mouse; only this stops
  // the keyboard.
  //
  // 🔴 `:not([disabled])` is the part that cannot be copied from `ui-dialogs.tsx`. Every control
  // here is disabled while converting, and a trap that cycles through disabled buttons focuses
  // nothing at all — Tab would appear dead, then release focus to the page behind. Mid-conversion
  // the list is legitimately empty and we simply hold focus on the panel.
  useEffect(() => {
    // Give focus back to whatever had it when this is done, so dismissing the modal returns the
    // caret to the compose body rather than to the top of the document.
    const previous = document.activeElement as HTMLElement | null;
    const focusable = () => focusableIn(panel.current);
    focusable()[0]?.focus() ?? panel.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      // Escape keeps the originals — the same answer as the X and the explicit button, and ignored
      // while converting for the same reason both of those are disabled.
      if (e.key === 'Escape') { if (!busy) cancel(); return; }
      handleTrapKey(e, panel.current);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      previous?.focus?.();
    };
    // `busy` is read inside the handler, so the listener is rebound when it flips — that is what
    // makes Escape stop working the moment conversion starts.
  }, [busy]);

  async function run() {
    setBusy(true);
    const out: Attachment[] = [];
    const missed: string[] = [];
    // 🚩 Sequential, not Promise.all: sharp runs at concurrency 1 on a 4-core box shared with the
    // IMAP sync, so firing five at once only queues them while making the progress count meaningless.
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      patch(i, { phase: 'uploading', uploaded: 0 });
      try {
        const r = await optimizeImage(
          { filename: img.filename, format, content: img.content },
          // 🚩 Real bytes-sent, straight from xhr.upload. At 1 the upload is done and the server
          // starts work it reports nothing about, so the row flips to the indeterminate phase.
          (fraction) => patch(i, fraction >= 1 ? { phase: 'converting', uploaded: 1 } : { uploaded: fraction }),
        );
        if (r.converted && r.content && r.filename && r.contentType && r.size) {
          out.push({ filename: r.filename, contentType: r.contentType, content: r.content, size: r.size });
          patch(i, { phase: 'done', finalSize: r.size });
        } else {
          // Already small, or undecodable (a corrupt file — iPhone HEIC converts fine since the
          // heif-convert bridge). Keep the original: a success, not a failure, and it still attaches.
          out.push(img);
          patch(i, { phase: 'kept' });
          if (r.reason) missed.push(`${img.filename}: ${r.reason}`);
        }
      } catch (e) {
        // 🔒 A conversion that fails must never lose the attachment.
        out.push(img);
        patch(i, { phase: 'kept' });
        missed.push(`${img.filename}: ${(e as Error).message}`);
      }
    }
    finish([...passthrough, ...out], missed);
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={busy ? 'Shrinking images' : 'Shrink images'}
        tabIndex={-1}
        // Wider while converting: the ring plus a row per file needs the room, and the chooser
        // does not. Same panel, so the swap does not read as a different dialog opening.
        className={
          'w-full rounded-xl border border-bmail-border bg-bmail-surface shadow-xl focus:outline-none transition-[max-width] '
          + (busy ? 'max-w-lg p-7' : 'max-w-md p-5')
        }
      >
        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-bmail-text" />
            <h2 className="text-base font-semibold text-bmail-text">
              {busy
                ? `Shrinking ${images.length === 1 ? 'your image' : `${images.length} images`}`
                : required
                  ? 'Too large to attach as they are'
                  : `Shrink ${images.length === 1 ? 'this image' : `these ${images.length} images`}?`}
            </h2>
          </div>
          {/* 🔒 Hidden, not merely disabled, while converting. A disabled X is a dead target that
              invites clicking at exactly the moment the answer is "wait". The double-append this
              used to cause is guarded by `settled` either way; Escape is ignored in the same state. */}
          {!busy && (
            <button
              onClick={cancel}
              aria-label={required ? 'Cancel' : 'Close'}
              title={required ? 'Cancel — nothing will be attached' : 'Close'}
              className="rounded-lg p-1 text-bmail-text-secondary hover:bg-bmail-surface-hover"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {busy ? (
          <ImageProgress files={progress} format={format} />
        ) : (
          <>
            <p className="mb-4 text-sm text-bmail-text-secondary">
              {required ? (
                <>
                  {formatBytes(total)} of images is over the {formatBytes(MAX_TOTAL_ATTACHMENT_BYTES)}{' '}
                  limit for one email. Converting usually brings that down by most of its size, and the
                  picture still looks the same at a long edge of 2048&nbsp;px — so these will very
                  likely fit once converted.
                </>
              ) : (
                <>
                  {formatBytes(total)} attached. Converting usually saves most of that, and the picture
                  still looks the same at a long edge of 2048&nbsp;px.
                </>
              )}
            </p>

            <div className="mb-4 grid grid-cols-2 gap-2">
              {([
                ['jpeg', 'JPEG', 'Opens anywhere. Fast.'],
                ['avif', 'AVIF', 'About half the size. Slower, and needs a recent phone or desktop to open.'],
              ] as const).map(([v, label, note]) => (
                <button
                  key={v}
                  onClick={() => setFormat(v)}
                  aria-pressed={format === v}
                  className={
                    'rounded-lg border p-3 text-left '
                    + (format === v
                      ? 'border-bmail-goldfill bg-bmail-goldfill/10 text-bmail-text'
                      : 'border-bmail-border text-bmail-text-secondary hover:bg-bmail-surface-hover')
                  }
                >
                  <div className="text-sm font-medium">{label}</div>
                  <div className="mt-0.5 text-xs leading-snug">{note}</div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-end gap-2">
              <button
                onClick={required ? cancel : keepOriginals}
                className="rounded-lg px-3 py-2 text-sm text-bmail-text-secondary hover:bg-bmail-surface-hover"
              >
                {/* 🔒 Over the cap, "attach as they are" is not an available outcome — offering it
                    would be a button that can only fail. Cancelling attaches nothing instead. */}
                {required ? 'Cancel' : 'Attach as they are'}
              </button>
              <button
                onClick={run}
                className="rounded-lg bg-bmail-goldfill px-3 py-2 text-sm font-medium text-bmail-on-accent transition-colors hover:bg-bmail-goldfill-hover"
              >
                Convert to {format === 'avif' ? 'AVIF' : 'JPEG'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}
